import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useQuery, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PageBreadcrumb } from '@repo/shared-client/components/blocks'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import SFSpinner from '@web/src/components/primatives/sf-spinner/sf-spinner'
import EventAttendance from '@web/src/features/event/event-attendance/event-attendance'
import EventBadges from '@web/src/features/event/event-badges/event-badges'
import EventCancel from '@web/src/features/event/event-cancel/event-cancel'
import EventCloneModal from '@web/src/features/event/event-clone-modal/event-clone-modal'
import EventDelete from '@web/src/features/event/event-delete/event-delete'
import EventDetails from '@web/src/features/event/event-details/event-details'
import EventSummary from '@web/src/features/event/event-summary/event-summary'
import EventTransferModal from '@web/src/features/event/event-transfer-modal/event-transfer-modal'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import MediaUploaderDialog from '@web/src/features/media/media-uploader-dialog/media-uploader-dialog'
import SimpleMediaImageViewer from '@web/src/features/media/simple-media-image-viewer/simple-media-image-viewer'
import Posts from '@web/src/features/post/posts/posts'
//import JsonLd from 'src/features/primatives/json-ld/json-ld'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import { useAuth } from '@web/src/hooks/use-auth'
import { useIsCurrentTimezone } from '@web/src/hooks/use-is-current-timezone'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { AttachedMediaInfo, EventInfo, ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { getTimezoneAbbreviation, limitBadgeNumber } from '@web/src/lib/utilsClient'
import { ensureAbsoluteWebUrl } from '@web/src/lib/web-url'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  CircleAlertIcon,
  CircleDollarSignIcon,
  ClockIcon,
  EllipsisIcon,
  FilmIcon,
  GlobeIcon,
  LinkIcon,
  TimerOffIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  DeleteAttachedMediaMutation,
  DeleteAttachedMediaMutationVariables,
  EventHostAttendeesQuery,
  EventHostAttendeesQueryVariables,
  EventQuery,
  EventQueryVariables,
} from 'types/graphql'
import { eventPageHeadFromLoaderData } from './$eventIdOrSlug.seo'

type EventPageData = NonNullable<EventQuery['event']>
type EventAttachedMedia = NonNullable<EventPageData['attachedMedia']>[number]

// Initialize dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const EVENT_QUERY: TypedDocumentNode<EventQuery, EventQueryVariables> = gql`
  query EventQuery($eventIdOrSlug: String!) {
    event: event(eventIdOrSlug: $eventIdOrSlug) {
      ...EventInfo
      owner {
        ...ProfileBasicInfo
      }
      attachedMedia {
        ...AttachedMediaInfo
      }
      myAttendance {
        id
        peopleQty
        notes
        status
        role
      }
      myJoinRequest {
        id
        status
      }
      myInvitation {
        id
      }
    }
  }
  ${EventInfo}
  ${AttachedMediaInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const DELETE_IMAGE: TypedDocumentNode<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables> = gql`
  mutation DeleteAttachedMedia($id: String!) {
    deleteAttachedMedia(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_UP = gql`
  mutation MoveAttachedMediaUp($id: String!) {
    moveAttachedMediaUp(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_DOWN = gql`
  mutation MoveAttachedMediaDown($id: String!) {
    moveAttachedMediaDown(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_TO_TOP = gql`
  mutation MoveAttachedMediaToTop($id: String!) {
    moveAttachedMediaToTop(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_TO_BOTTOM = gql`
  mutation MoveAttachedMediaToBottom($id: String!) {
    moveAttachedMediaToBottom(id: $id) {
      id
    }
  }
`

export const HOST_ATTENDEES_QUERY: TypedDocumentNode<EventHostAttendeesQuery, EventHostAttendeesQueryVariables> = gql`
  query EventHostAttendeesQuery($page: Int, $filter: EventAttendeeFilter, $sortBy: EventAttendeeSortEnum) {
    eventAttendeeListPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        profile {
          ...ProfileBasicInfo
        }
        role
        status
      }
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const Route = createFileRoute('/(public)/(main-layout)/event/$eventIdOrSlug')({
  component: EventPage,

  head: ({ loaderData }) => eventPageHeadFromLoaderData(loaderData),

  loader: async ({ context, params }) => {
    const { queryData: eventData, queryRef: eventQueryRef } = await queryAndPreload(context, EVENT_QUERY, {
      variables: { eventIdOrSlug: params.eventIdOrSlug },
    })

    return {
      queryRef: eventQueryRef,
      eventQueryData: eventData,
      event: eventData?.event,
    }
  },
})

const galleryThumbCardClass =
  'cursor-pointer border border-border bg-muted w-full transition-colors hover:border-primary hover:bg-muted/80'

type ImageCardProps = {
  onRefetch: () => void | Promise<void>
  event: EventPageData
  media: EventAttachedMedia
  idx: number
  onImageSelected: (idx: number) => void
  totalMedia: number
}

const ImageCard = ({ onRefetch, event, media, idx, onImageSelected, totalMedia }: ImageCardProps) => {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [adminEditModal, setAdminEditModal] = useState(false)

  const [deleteImage] = useMutation(DELETE_IMAGE, {
    onCompleted: () => {
      toast.success(t('Image Deleted'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveUp] = useMutation(MOVE_MEDIA_UP, {
    onCompleted: () => {
      toast.success(t('Media moved up'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveDown] = useMutation(MOVE_MEDIA_DOWN, {
    onCompleted: () => {
      toast.success(t('Media moved down'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToTop] = useMutation(MOVE_MEDIA_TO_TOP, {
    onCompleted: () => {
      toast.success(t('Media moved to top'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToBottom] = useMutation(MOVE_MEDIA_TO_BOTTOM, {
    onCompleted: () => {
      toast.success(t('Media moved to bottom'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleDeleteImage = () => {
    setDeleteModal(false)
    deleteImage({ variables: { id: media.id } })
  }

  return (
    <div className="relative shrink-0">
      <AspectRatio className="relative w-[125px] min-w-[125px]" ratio={1}>
        <Card className={cn(galleryThumbCardClass, 'h-full overflow-hidden p-0')} onClick={() => onImageSelected(idx)}>
          <SFMediaImage
            imageProps={{
              alt: t('{{eventName}} — additional gallery image', { eventName: event.name }),
            }}
            media={media}
          />
        </Card>
      </AspectRatio>
      {event.canDeleteMedia ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="absolute top-0.5 right-0.5 z-10"
            onClick={(e) => e.stopPropagation()}
            render={
              <Button
                aria-label={t('Media options')}
                className="rounded-full border-black"
                size="icon-xs"
                type="button"
                variant="default"
              />
            }
          >
            <EllipsisIcon className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveToTop({ variables: { id: media.id } })}>
                {t('Move to Top')}
              </DropdownMenuItem>
            ) : null}
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveUp({ variables: { id: media.id } })}>
                {t('Move Up')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveDown({ variables: { id: media.id } })}>
                {t('Move Down')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveToBottom({ variables: { id: media.id } })}>
                {t('Move to Bottom')}
              </DropdownMenuItem>
            ) : null}

            <MediaAdminEditMenuItems mediaId={media.id} onOpen={() => setAdminEditModal(true)} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDeleteModal(true)} variant="destructive">
              {t('Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <ConfirmDialog
        onConfirm={handleDeleteImage}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this image?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <MediaAdminEditDialog
        isAttached
        mediaId={media.id}
        onMediaChange={onRefetch}
        onOpenChange={(e) => setAdminEditModal(e.open)}
        open={adminEditModal}
      />
    </div>
  )
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Route page with many conditional sections; splitting would obscure loader/query coupling.
function EventPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const client = useApolloClient()
  const { data, error } = useReadQuery(loaderData.queryRef as QueryRef<EventQuery>)
  const navigate = useNavigate()
  const [uploadImageModal, setUploadImageModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [reportModal, setReportModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [transferModal, setTransferModal] = useState(false)
  const [cloneModal, setCloneModal] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)
  const [activeTab, setActiveTab] = useState('details')
  const { isAuthenticated } = useAuth()

  const event = (data ?? loaderData.eventQueryData)?.event ?? loaderData.event ?? undefined
  const isTimezoneMatching = useIsCurrentTimezone(event?.timezone)

  // Query for host attendees - must be called before early returns (not preloaded in the route loader).
  const { data: hostAttendeesData, loading: hostAttendeesLoading } = useQuery(HOST_ATTENDEES_QUERY, {
    variables: {
      page: 1,
      filter: {
        eventId: event?.id || '',
        role: 'HOST',
      },
      sortBy: 'OLDEST',
    },
    skip: !event?.id,
  })

  // Create a refetch function that uses Apollo Client directly to avoid router reloads
  // This refetches the query without triggering TanStack Router's loader system
  const handleRefetch = async () => {
    await client.refetchQueries({ include: [EVENT_QUERY] })
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!event) {
    return <ErrorAlert error={new Error(t('Event not found'))} />
  }

  // Format dates with timezone. Abbreviation is client-only (see useIsCurrentTimezone).
  const formatEventDate = (dateString: string | null | undefined, eventTimezone?: string | null) => {
    if (!dateString) return ''

    let tzDate = dayjs(dateString)

    if (event?.timezone) {
      tzDate = tzDate.tz(event.timezone)
    }

    const formattedDate =
      tzDate.year() === dayjs().year()
        ? tzDate.format('ddd, MMM D [at] h:mm a')
        : tzDate.format('ddd, MMM D [at] h:mm a, YYYY')

    if (eventTimezone && !isTimezoneMatching) {
      const tzAbbr = getTimezoneAbbreviation(eventTimezone)
      return `${formattedDate} ${tzAbbr}`
    }

    return formattedDate
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col items-stretch gap-3">
        <PageBreadcrumb
          items={[
            { label: t('Home'), to: '/' },
            { label: t('Event Categories'), to: '/event' },
            { label: t('Discover Events'), to: '/event/list' },
            { label: event.name },
          ]}
        />

        <div className="flex w-full flex-row flex-wrap items-start justify-between gap-3">
          <h1 className="min-w-0 flex-1 truncate text-center font-bold text-2xl md:text-left md:text-3xl">
            {event?.name}
          </h1>

          <div className="flex shrink-0 justify-center md:justify-end">
            {event.canDelete || event.canEdit || event.canReport || event.canCancel ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="transition-all duration-300"
                  render={
                    <Button
                      aria-label={t('Event options')}
                      className="shadow-none"
                      size="icon"
                      type="button"
                      variant="default"
                    />
                  }
                >
                  <EllipsisIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {event.canCancel ? (
                    <DropdownMenuItem onClick={() => setCancelModal(true)}>{t('Cancel')}</DropdownMenuItem>
                  ) : null}
                  {event.canDelete ? (
                    <DropdownMenuItem onClick={() => setDeleteModal(true)}>{t('Delete')}</DropdownMenuItem>
                  ) : null}
                  {event.canEdit ? (
                    <DropdownMenuItem
                      onClick={() =>
                        navigate({
                          to: '/event/$eventIdOrSlug/edit',
                          params: { eventIdOrSlug: event.slug || event.id },
                        } as never)
                      }
                    >
                      {t('Edit')}
                    </DropdownMenuItem>
                  ) : null}
                  {event.canReport ? (
                    <DropdownMenuItem onClick={() => setReportModal(true)}>{t('Report')}</DropdownMenuItem>
                  ) : null}
                  {event.canTransfer ? (
                    <DropdownMenuItem onClick={() => setTransferModal(true)}>{t('Transfer')}</DropdownMenuItem>
                  ) : null}
                  {event.canEdit ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() =>
                          navigate({
                            to: '/event/$eventIdOrSlug/pdf-export',
                            params: { eventIdOrSlug: event.slug || event.id },
                          } as never)
                        }
                      >
                        {t('Export PDF')}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                  {event.canClone ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setCloneModal(true)}>{t('Clone Event')}</DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>

        {event.status === 'CANCELLED' ? (
          <Alert variant="destructive">
            <CircleAlertIcon />
            <AlertTitle>{t('Event has been cancelled.')}</AlertTitle>
            {event.cancellationReason ? (
              <AlertDescription className="whitespace-pre-line">{event.cancellationReason}</AlertDescription>
            ) : null}
          </Alert>
        ) : null}

        <h2 className="flex w-full justify-start font-normal text-base leading-snug">
          <span className="line-clamp-2">{event?.tagLine}</span>
        </h2>

        <EventBadges
          admission={event?.admission}
          category={event?.category}
          size="md"
          tags={event?.tags}
          variant="subtle"
          visibility={event?.visibility}
        />

        <Separator className="w-full" />

        {hostAttendeesLoading ? (
          <div className="flex w-full justify-center py-2">
            <SFSpinner size="sm" />
          </div>
        ) : (
          <div className="flex w-full flex-wrap gap-x-6 gap-y-2">
            {hostAttendeesData?.eventAttendeeListPage?.entries.map((attendee) => (
              <SFProfileBanner
                className="w-fit shrink-0"
                key={attendee.id}
                lowerTitle={t('Host')}
                profile={attendee.profile}
                size="md"
              />
            ))}
          </div>
        )}

        <Separator className="w-full" />

        <Card>
          <CardContent className="flex flex-col items-start gap-3">
            <div className="flex w-full flex-row items-center justify-start gap-2">
              <UsersIcon className="size-5 shrink-0" />
              <span className="line-clamp-1 min-w-0 text-base">
                {t('{{count}} Going', { count: event?.attendanceCounts?.goingCount })}
                {event?.attendanceCounts?.maybeCount > 0 &&
                  t(' / {{count}} Maybe', { count: event?.attendanceCounts?.maybeCount })}
                {event?.attendanceCounts?.waitlistCount > 0 &&
                  t(' / {{count}} Waitlist', { count: event?.attendanceCounts?.waitlistCount })}
                {event?.capacity && event?.waitlistEnabled && t(' / {{count}} Capacity', { count: event?.capacity })}
              </span>
            </div>

            {event.admission ? (
              <div className="flex w-full flex-col items-start gap-1">
                <div className="flex w-full flex-row items-center justify-start gap-2">
                  <CircleDollarSignIcon className="size-5 shrink-0" />
                  <span className="font-medium">{t('Admission Fees: Required')}</span>
                </div>
                <div className="grid w-full grid-cols-[auto_auto_1fr] gap-1 text-base">
                  {event?.admissionCouples !== null ? (
                    <>
                      <div className="w-6" />
                      <span className="line-clamp-1 text-muted-foreground">{t('Couples:')}</span>
                      <span className="line-clamp-1">$ {event?.admissionCouples?.toFixed(2)}</span>
                    </>
                  ) : null}
                  {event?.admissionMale !== null ? (
                    <>
                      <div className="w-6" />
                      <span className="line-clamp-1 text-muted-foreground">{t('Single Male:')}</span>
                      <span className="line-clamp-1">$ {event?.admissionMale?.toFixed(2)}</span>
                    </>
                  ) : null}
                  {event?.admissionFemale !== null ? (
                    <>
                      <div className="w-6" />
                      <span className="line-clamp-1 text-muted-foreground">{t('Single Female:')}</span>
                      <span className="line-clamp-1">$ {event?.admissionFemale?.toFixed(2)}</span>
                    </>
                  ) : null}

                  {event?.admissionNote || event?.privateAdmissionNote ? (
                    <>
                      <div className="w-6" />
                      <span className="line-clamp-1 text-muted-foreground">{t('Admission Notes:')}</span>
                      <div className="flex min-w-0 flex-col items-start gap-1">
                        {event.admissionNote ? (
                          <span className="whitespace-pre-line">{event.admissionNote}</span>
                        ) : null}
                        {event?.privateAdmissionNote ? (
                          <span className="whitespace-pre-line">{event.privateAdmissionNote}</span>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}

            {event.timezone && !isTimezoneMatching ? (
              <>
                <div className="flex w-full flex-row items-center justify-start gap-2">
                  <GlobeIcon className="size-5 shrink-0" />
                  <div className="flex min-w-0 flex-row items-center gap-2">
                    <span className="line-clamp-1">{t('Timezone:')}</span>
                    <span className="line-clamp-1 font-bold">{`${event.timezone} / ${getTimezoneAbbreviation(event.timezone)}`}</span>
                  </div>
                </div>
                {isAuthenticated ? (
                  <Badge className="ml-7" size="sm" variant="secondary">
                    {t('Different from your timezone')}
                  </Badge>
                ) : null}
              </>
            ) : null}

            <div className="flex w-full flex-row items-center justify-start gap-2">
              <ClockIcon className="size-5 shrink-0" />
              <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
                <span>{t('From:')}</span>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        className="line-clamp-1 min-w-0 max-w-full cursor-default border-0 bg-transparent p-0 text-left font-medium text-base text-foreground"
                        type="button"
                      >
                        {formatEventDate(event.startDate, event.timezone)}
                      </button>
                    }
                  />
                  <TooltipContent>
                    {event.timezone
                      ? t('Timezone: {{timezone}}', { timezone: event.timezone })
                      : t('No timezone specified')}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {event?.endDate ? (
              <div className="flex w-full flex-row items-center justify-start gap-2">
                <TimerOffIcon className="size-5 shrink-0" />
                <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
                  <span>{t('Until:')}</span>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          className="line-clamp-1 min-w-0 max-w-full cursor-default border-0 bg-transparent p-0 text-left font-medium text-base text-foreground"
                          type="button"
                        >
                          {formatEventDate(event.endDate, event.timezone)}
                        </button>
                      }
                    />
                    <TooltipContent>
                      {event.timezone
                        ? t('Timezone: {{timezone}}', { timezone: event.timezone })
                        : t('No timezone specified')}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ) : null}

            {event.webLinks?.length > 0 ? (
              <>
                <Separator className="w-full border-border/50" />
                {event.webLinks?.map((link, idx) => {
                  const href = ensureAbsoluteWebUrl(link)
                  return (
                    <div className="flex w-full flex-col items-start" key={idx}>
                      <Link
                        className="inline-flex min-w-0 max-w-full items-center gap-2 font-semibold text-primary"
                        rel="noopener noreferrer"
                        target="_blank"
                        to={href}
                      >
                        <LinkIcon className="size-4 shrink-0" />
                        <span className="line-clamp-1">{link}</span>
                      </Link>
                    </div>
                  )
                })}
              </>
            ) : null}
          </CardContent>
        </Card>

        {event.canAttend && <EventAttendance event={event} onStatusChange={handleRefetch} />}

        {event.canSeeAttendees || event.canEdit ? (
          <div className="flex w-full flex-col items-start gap-2">
            <Separator className="w-full" />

            <div className="flex w-full flex-row flex-wrap items-center justify-center gap-2 md:justify-start">
              {event.canSeeAttendees ? (
                <Button
                  onClick={() =>
                    navigate({
                      to: '/event/$eventIdOrSlug/attendees',
                      params: { eventIdOrSlug: event.slug || event.id },
                    } as never)
                  }
                  type="button"
                  variant="outline"
                >
                  <UsersIcon />
                  {t('Attendees')}
                  {event.canEdit && event.joinRequestCounts.pendingCount > 0 ? (
                    <Badge className="rounded-full">{limitBadgeNumber(event.joinRequestCounts.pendingCount)}</Badge>
                  ) : null}
                </Button>
              ) : null}

              {event?.canInvite ? (
                <Button
                  onClick={() =>
                    navigate({
                      to: '/event/$eventIdOrSlug/invite',
                      params: { eventIdOrSlug: event.slug || event.id },
                    } as never)
                  }
                  type="button"
                >
                  <UserPlusIcon />
                  {t('Invite')}
                </Button>
              ) : null}
            </div>

            <Separator className="w-full" />
          </div>
        ) : null}

        <Tabs className="w-full" onValueChange={setActiveTab} value={activeTab}>
          <TabsList className="mb-2 w-full max-w-full overflow-x-auto overflow-y-hidden" variant="tabs">
            <TabsTrigger value="details">{t('Details')}</TabsTrigger>
            {event.canEdit && event.admission ? <TabsTrigger value="summary">{t('Host Summary')}</TabsTrigger> : null}
          </TabsList>

          <TabsContent className="w-full" value="details">
            <div className="flex w-full flex-col gap-3">
              <div className="flex w-full flex-col">
                {event?.attachedMedia?.length > 0 ? (
                  <div className="flex w-full flex-row items-start gap-3">
                    {event?.attachedMedia?.map((attachedMedia, idx) => {
                      if (idx > 0) {
                        return null
                      }

                      return (
                        <Card
                          className={cn(
                            galleryThumbCardClass,
                            'w-full overflow-hidden p-0 md:w-[40%]',
                            idx > 0 ? 'hidden md:block' : ''
                          )}
                          key={attachedMedia.id}
                          onClick={() => {
                            setImageIndex(idx)
                            setViewModal(true)
                          }}
                        >
                          <AspectRatio
                            ratio={
                              attachedMedia.width && attachedMedia.height
                                ? attachedMedia.width / attachedMedia.height
                                : 316 / 480
                            }
                          >
                            <SFMediaImage
                              backgroundSize="contain"
                              imageProps={{
                                alt:
                                  idx > 0
                                    ? t('{{eventName}} — secondary hero image', { eventName: event.name })
                                    : t('{{eventName}} — primary hero image', { eventName: event.name }),
                              }}
                              media={attachedMedia}
                            />
                          </AspectRatio>
                        </Card>
                      )
                    })}
                  </div>
                ) : null}

                <div className="flex w-full flex-row items-start justify-start gap-2 overflow-x-auto overflow-y-hidden pt-2">
                  {event.canUploadMedia ? (
                    <AspectRatio className="w-[125px] min-w-[125px] shrink-0" ratio={1}>
                      <Card
                        className={cn(galleryThumbCardClass, 'flex h-full w-full flex-col p-0')}
                        onClick={() => setUploadImageModal(true)}
                      >
                        <CardContent className="flex h-full flex-col items-center justify-center gap-2 p-2 text-center">
                          <FilmIcon className="size-8 shrink-0" />
                          <span className="text-base leading-tight">
                            {t('Upload')}
                            <br />
                            {t('Media')}
                          </span>
                        </CardContent>
                      </Card>
                    </AspectRatio>
                  ) : null}

                  {event.attachedMedia?.map((attachedMedia, idx) => {
                    return (
                      <ImageCard
                        event={event}
                        idx={idx}
                        key={attachedMedia.id}
                        media={attachedMedia}
                        onImageSelected={(selectedIdx) => {
                          setImageIndex(selectedIdx)
                          setViewModal(true)
                        }}
                        onRefetch={handleRefetch}
                        totalMedia={event.attachedMedia.length}
                      />
                    )
                  })}
                </div>
              </div>

              <EventDetails event={event} />

              {isAuthenticated ? (
                <Posts
                  disablePoints={true}
                  eventId={event?.id}
                  filterType="EVENT"
                  groupId={event?.groupId}
                  noSideBars={true}
                  onPostChange={handleRefetch}
                  showSort={true}
                  showWhatsNew={event.canPost}
                />
              ) : (
                <div className="flex justify-center py-6">
                  <SFJoinButton />
                </div>
              )}
            </div>
          </TabsContent>

          {event.canEdit && event.admission ? (
            <TabsContent className="w-full" value="summary">
              <EventSummary event={event} eventId={event.id} />
            </TabsContent>
          ) : null}
        </Tabs>

        <EventTransferModal
          eventIdOrSlug={event.slug || event.id}
          onComplete={handleRefetch}
          onOpenChange={(e) => setTransferModal(e.open)}
          open={transferModal}
        />

        <EventDelete event={event} onOpenChange={(e) => setDeleteModal(e.open)} open={deleteModal} />

        <EventCancel event={event} onOpenChange={(e) => setCancelModal(e.open)} open={cancelModal} />

        <ReportCreate
          onOpenChange={(e) => setReportModal(e.open)}
          open={reportModal}
          targetId={event?.id}
          targetKind="EVENT"
        />

        <MediaUploaderDialog
          eventId={event.id}
          onOpenChange={(e) => setUploadImageModal(e.open)}
          onUploadComplete={handleRefetch}
          open={uploadImageModal}
        />

        {event.attachedMedia && (
          <SimpleMediaImageViewer
            attachedMedia={true}
            imageIndex={imageIndex}
            mediaArray={event.attachedMedia}
            onMediaChange={handleRefetch}
            onOpenChange={(e) => setViewModal(e.open)}
            open={viewModal}
          />
        )}

        {/* Add the EventCloneModal component */}
        <EventCloneModal
          eventIdOrSlug={event.slug || event.id}
          eventName={event.name}
          onOpenChange={(e) => setCloneModal(e.open)}
          open={cloneModal}
        />
      </div>
    </div>
  )
}
